import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PrivacyTipRoundedIcon from '@mui/icons-material/PrivacyTipRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { RegistrationMode } from '../../model/settingsTypes';
import { useCustomerSettings, useUpdateCustomerSettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import {
  ChipListField,
  FeedbackSnackbar,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
} from '../components';
import { REGISTRATION_MODE_OPTIONS } from '../options';

/** Customer profile, registration, verification and personal-data settings. */
export function CustomerSettingsPage() {
  const { t } = useTranslation('settings');
  const { canView, canManage } = useDomainPermissions('customers');
  const query = useCustomerSettings();
  const update = useUpdateCustomerSettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success(t('customerSettings.savedFeedback')),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title={t('customerSettings.title')}
      subtitle={t('customerSettings.subtitle')}
      icon={<GroupsRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title={t('customerSettings.registrationSection.title')} icon={<GroupsRoundedIcon />}>
              <SelectField
                label={t('customerSettings.registrationSection.modeLabel')}
                value={draft.registrationMode}
                options={REGISTRATION_MODE_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                onChange={(value) => form.setField('registrationMode', value as RegistrationMode)}
                disabled={disabled}
              />
              <ChipListField
                label={t('customerSettings.registrationSection.requiredFieldsLabel')}
                value={draft.requiredProfileFields}
                onChange={(value) => form.setField('requiredProfileFields', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('customerSettings.registrationSection.addressBookLabel')}
                description={t('customerSettings.registrationSection.addressBookDescription')}
                checked={draft.addressBookEnabled}
                onChange={(value) => form.setField('addressBookEnabled', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title={t('customerSettings.verificationSection.title')} icon={<VerifiedUserRoundedIcon />}>
              <SwitchField
                label={t('customerSettings.verificationSection.phoneLabel')}
                checked={draft.phoneVerification}
                onChange={(value) => form.setField('phoneVerification', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('customerSettings.verificationSection.emailLabel')}
                checked={draft.emailVerification}
                onChange={(value) => form.setField('emailVerification', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title={t('customerSettings.personalDataSection.title')} icon={<PrivacyTipRoundedIcon />}>
              <SwitchField
                label={t('customerSettings.personalDataSection.consentLabel')}
                description={t('customerSettings.personalDataSection.consentDescription')}
                checked={draft.personalDataConsentRequired}
                onChange={(value) => form.setField('personalDataConsentRequired', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('customerSettings.personalDataSection.anonymizationLabel')}
                description={t('customerSettings.personalDataSection.anonymizationDescription')}
                checked={draft.anonymizationEnabled}
                onChange={(value) => form.setField('anonymizationEnabled', value)}
                disabled={disabled}
              />
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
